import { CSSProperties, MouseEvent, ReactNode } from 'react';
import FocusLock from 'react-focus-lock';
import { Icon } from 'components/common/icons';
import { useEscapeKey, useReduxState } from 'hooks';

type HideHandler = (event?: KeyboardEvent | MouseEvent) => void;

interface Props {
  autoFocus?: boolean;
  children?: ReactNode;
  closeButton?: boolean;
  header?: boolean;
  onContainerClick?: HideHandler;
  onHide?: HideHandler;
  overflow?: 'hidden' | 'visible';
  stopPropagation?: boolean;
  style?: CSSProperties;
  title?: ReactNode;
  visible: boolean;
  width?: 'medium' | 'narrow' | 'small' | 'wide';
}

const Modal = ({
  autoFocus = true,
  children,
  closeButton,
  header = true,
  onContainerClick,
  onHide,
  overflow = 'hidden',
  stopPropagation,
  style,
  title,
  visible,
  width = 'small'
}: Props) => {
  const { messages, theme } = useReduxState();

  const hideFromContainer = onContainerClick || onHide;
  useEscapeKey(visible, hideFromContainer);

  const handleClick = (event: MouseEvent) => event.stopPropagation();

  const handleCloseClick = (event: MouseEvent) => onHide?.(event);

  const handleContainerClick = (event: MouseEvent) => {
    if (stopPropagation) event.stopPropagation();
    hideFromContainer?.(event);
  };

  return (
    <FocusLock autoFocus={autoFocus} disabled={!visible}>
      <div
        className={`modal-container ${visible && 'visible'}`}
        onClick={handleClick}
      >
        {visible ? (
          <>
            <div
              className={`modal-container-background`}
              onClick={handleContainerClick}
            />
            <div
              aria-modal={true}
              className={`modal${
                overflow === 'visible' ? ' modal--overflow-visible' : ''
              }`}
              role="dialog"
              style={{
                maxWidth:
                  width === 'wide'
                    ? 920
                    : width === 'medium'
                    ? 640
                    : width === 'narrow'
                    ? 280
                    : 400, // small
                ...style
              }}
            >
              {header ? (
                <div className="modal-header">
                  <div className="modal-title">{title}</div>
                  {closeButton && onHide ? (
                    <button
                      aria-label={messages.commonClose}
                      className="close focus-box-shadow"
                      onClick={handleCloseClick}
                    >
                      <Icon icon="times" style={{ fontSize: '1.6rem' }} />
                    </button>
                  ) : null}
                </div>
              ) : null}
              {children}
            </div>
          </>
        ) : null}

        <style jsx>{`
          .modal-container {
            align-items: center;
            display: flex;
            justify-content: center;
            min-width: 320px;
            position: fixed;
            top: 0;
            right: 0;
            bottom: 0;
            left: 0;
            visibility: hidden;
            z-index: -1;
          }
          .modal-container.visible {
            visibility: visible;
            z-index: 220;
          }
          .modal-container-background {
            background-color: rgba(0, 0, 0, 0.5);
            top: 0;
            bottom: 0;
            left: 0;
            right: 0;
            position: absolute;
            opacity: 0;
          }
          .modal-container.visible .modal-container-background {
            animation: containerappear 0.2s ease-out;
            opacity: 1;
          }
          .modal {
            background-color: white;
            border-radius: ${theme.cardBorderRadius};
            display: flex;
            flex-direction: column;
            max-height: calc(100% - 100px);
            min-width: 280px;
            opacity: 0;
            overflow: ${overflow};
            position: relative;
            text-align: left;
            top: 20px;
            width: calc(100% - 30px);
          }
          .modal-container.visible .modal {
            animation: modalappear 0.2s ease-out;
            opacity: 1;
            top: -20px;
          }
          .modal-header {
            background-color: ${theme.subHeaderBackgroundColor};
            padding: 10px 15px;
            position: relative;
          }
          .modal-title {
            color: ${theme.subHeaderColorActive};
            font-size: 1.6rem;
            font-weight: 700;
            margin: 0;
            padding-right: ${closeButton ? '25px' : '0px'};
          }
          .close {
            background-color: transparent;
            border: 0;
            border-radius: 5px;
            color: ${theme.subHeaderColor};
            cursor: pointer;
            display: inline-block;
            flex-grow: 0;
            flex-shrink: 0;
            outline: 0;
            padding: 8px 10px;
            position: absolute;
            right: 5px;
            top: 4px;
            transition: color 0.3s;
          }
          .close:hover {
            color: ${theme.subHeaderColorActive};
          }
          .close:focus {
            box-shadow: 0 0 4px 4px rgba(0, 0, 0, 0.15);
          }
          .modal--overflow-visible .modal-header {
            border-top-left-radius: ${theme.cardBorderRadius};
            border-top-right-radius: ${theme.cardBorderRadius};
          }
          @keyframes containerappear {
            from {
              opacity: 0;
            }
            to {
              opacity: 1;
            }
          }
          @keyframes modalappear {
            from {
              opacity: 0;
              top: 20px;
            }
            to {
              opacity: 1;
              top: -20px;
            }
          }
        `}</style>
      </div>
    </FocusLock>
  );
};

export default Modal;
